import { useCallback, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { isBackendErrorCode, isServerErr, type ServerError } from "@/lib/api/errors/ErrorHandler";
import { describeBatchUpdateError } from "@/lib/i18n/translateBackendError";
import { describeBulkFailures } from "@/lib/bulk/bulkErrorMessage";

export type ActionResult<T> =
  | { ok: true; value: T }
  /** `error: null` — the call was ignored because one was already running. */
  | { ok: false; error: string | null };

export type RunOptions<T> = {
  /** The site's own sentence for a failure we have no copy for (R1). */
  fallback: string;
  /**
   * For a call that RESOLVES on a refusal — a bulk outcome with `failed > 0`,
   * a `{created, cardsFailed}` pair: return the part that failed (it is read
   * like any other cause). Any falsy answer — `null`, `undefined`, `false`,
   * `0`, `""` — is a success, so a predicate (`o => o.failed > 0`) works too
   * (`true` then reads as the site fallback).
   */
  failureOf?: (value: T) => unknown;
};

/**
 * One idiom for a component action with several ways out — success, a partial
 * refusal, a failure (plan graceful-giggling-melody §1).
 *
 * `run(fn, {fallback})` never throws. It answers `{ok: true, value}` or
 * `{ok: false, error}` with the sentence already in `error`, so the caller
 * closes its dialog only on `ok` (R3) and renders `error` where the dialog
 * shows errors. It does not close, toast or navigate by itself — that stays
 * visible at the call site.
 *
 * Why the hook and not a hand-written try/catch: the hand-written ones kept
 * getting the same three things wrong. A `void | ServerError` handler resolves
 * on a refusal, so `await h(); onClose()` closed the dialog on failure. The
 * reason the server named (a per-row `errorCode`, a bulk `reason`) was replaced
 * by the generic retry line. And a second click while the first request ran
 * sent it twice.
 *
 * The sentence, one ladder for every cause (R7a): per-row `errorCode`s →
 * describeBatchUpdateError; per-row bulk `reason`s → their sentence
 * (bulkConfirm.reason.*, then errors.*), the site's fallback for a reason we
 * have none for; otherwise the code via translateBackendError with the site's
 * fallback.
 */
export function useActionError() {
  const tErrors = useTranslations("errors");
  const tBulk = useTranslations("bulkConfirm");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The state above renders; this ref is what a second click in the same tick
  // sees (state would still read the old value).
  const running = useRef(false);

  const describe = useCallback(
    (cause: unknown, fallback: string): string => {
      const failures =
        cause && typeof cause === "object"
          ? (cause as { failures?: unknown }).failures
          : undefined;
      if (Array.isArray(failures)) {
        // A malformed row (null, a string) must not make `run` throw after all.
        const rows = (failures as unknown[]).filter(
          (r): r is { errorCode?: unknown; reason?: unknown } => !!r && typeof r === "object",
        );
        const hasCode = rows.some((r) => typeof r.errorCode === "string");
        const hasReason = rows.some((r) => typeof r.reason === "string");
        if (!hasCode && hasReason) {
          const reasons = rows.map((r) => ({
            reason: typeof r.reason === "string" ? r.reason : undefined,
          }));
          return describeBulkFailures(reasons, { tErrors, tBulk })[0] ?? fallback;
        }
        if (hasCode) {
          // Rebuilt rather than spread: an Error's `message` is not enumerable.
          const c = cause as { message?: unknown; detail?: unknown };
          return describeBatchUpdateError(
            tErrors,
            { message: c.message, detail: c.detail, failures: rows },
            fallback,
          );
        }
      }
      // A ServerError, an Error, a plain `{message}` object or a bare code
      // string — all readable; anything else reads as the site's fallback.
      const readable =
        typeof cause === "string" ||
        isServerErr(cause) ||
        cause instanceof Error ||
        (!!cause &&
          typeof cause === "object" &&
          typeof (cause as { message?: unknown }).message === "string")
          ? (cause as string | { message?: string; detail?: string })
          : null;
      return describeBatchUpdateError(tErrors, readable, fallback);
    },
    [tErrors, tBulk],
  );

  const setFrom = useCallback(
    (cause: unknown, fallback: string) => setError(describe(cause, fallback)),
    [describe],
  );

  const clear = useCallback(() => setError(null), []);

  const run = useCallback(
    async <T>(
      fn: () => Promise<T | ServerError>,
      { fallback, failureOf }: RunOptions<T>,
    ): Promise<ActionResult<T>> => {
      if (running.current) return { ok: false, error: null };
      running.current = true;
      setBusy(true);
      setError(null);
      try {
        const value = await fn();
        if (isServerErr(value)) {
          const text = describe(value, fallback);
          setError(text);
          return { ok: false, error: text };
        }
        const failed = failureOf ? failureOf(value as T) : null;
        if (failed) {
          const text = describe(failed, fallback);
          setError(text);
          return { ok: false, error: text };
        }
        return { ok: true, value: value as T };
      } catch (e) {
        // A thrown backend refusal is the product working; anything else
        // (a TypeError in the call) is a bug the user now sees as the
        // fallback — keep its stack in the console.
        if (!isServerErr(e) && !(e instanceof Error && isBackendErrorCode(e.message))) {
          console.error("[useActionError]", e);
        }
        const text = describe(e, fallback);
        setError(text);
        return { ok: false, error: text };
      } finally {
        running.current = false;
        setBusy(false);
      }
    },
    [describe],
  );

  return { error, busy, run, setFrom, clear };
}
